import { ZoomIn, ZoomOut } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentPage } from '@/attachment/hooks/useCurrentPage';
import { useDocumentWatermark } from '@/attachment/hooks/useDocumentWatermark';
import { usePdfRenderer } from '@/attachment/hooks/usePdfRenderer';
import { usePdfThumbnails } from '@/attachment/hooks/usePdfThumbnails';
import { usePdfZoom } from '@/attachment/hooks/usePdfZoom';
import { Button } from '@/common/components/ui/button';
import { Skeleton } from '@/common/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { DocumentLoadError } from './DocumentLoadError';
import { PageNavigatorToggle } from './PageNavigatorToggle';
import { PdfPagesScrollArea } from './PdfPagesScrollArea';
import { PdfThumbnailNavigator } from './PdfThumbnailNavigator';

export interface DocumentViewerOverlayContext {
  pageElements: Map<number, HTMLDivElement>;
  scale: number;
  scrollToPage: (pageNumber: number) => void;
}

interface DocumentViewerProps {
  blob: Blob;
  documentId: number;
  className?: string;
  defaultNavigatorVisible?: boolean;
  renderOverlay?: (context: DocumentViewerOverlayContext) => ReactNode;
}

export const DocumentViewer = ({
  blob,
  documentId,
  className,
  defaultNavigatorVisible = true,
  renderOverlay,
}: DocumentViewerProps) => {
  const { t } = useTranslation();
  const { scale, zoomPercentage, zoomIn, zoomOut, canZoomIn, canZoomOut } =
    usePdfZoom();
  const [isThumbnailNavigatorVisible, setIsThumbnailNavigatorVisible] =
    useState(defaultNavigatorVisible);
  const {
    containerRef,
    pageElements,
    pdfDocument,
    totalPages,
    hasError,
    isRendering,
  } = usePdfRenderer(blob, scale);
  const { thumbnails, isLoading: isThumbnailsLoading } = usePdfThumbnails(
    pdfDocument,
    totalPages,
  );
  const { currentPage, scrollToPage } = useCurrentPage(
    containerRef,
    pageElements,
  );
  const [viewer, setViewer] = useState<HTMLDivElement | null>(null);
  useDocumentWatermark(viewer, documentId);

  if (hasError) {
    return <DocumentLoadError className={className} />;
  }

  return (
    <div
      ref={setViewer}
      className={cn('relative flex h-full w-full bg-muted', className)}
    >
      {isThumbnailNavigatorVisible && (
        <PdfThumbnailNavigator
          thumbnails={thumbnails}
          currentPage={currentPage}
          onPageClick={scrollToPage}
          isLoading={isThumbnailsLoading}
          className="w-[150px] h-full shrink-0"
        />
      )}

      <div className="relative flex-1 min-w-0">
        <PageNavigatorToggle
          isNavigatorVisible={isThumbnailNavigatorVisible}
          onToggle={() =>
            setIsThumbnailNavigatorVisible(!isThumbnailNavigatorVisible)
          }
          className="absolute z-20 top-4 left-4 rounded-lg shadow-md"
        />

        <div className="absolute z-20 top-4 right-4 flex items-center gap-1 rounded-lg border bg-background px-1 shadow-md">
          <Button
            onClick={zoomOut}
            variant="ghost"
            size="icon-sm"
            aria-label={t('navigation.zoomOut')}
            disabled={!canZoomOut}
          >
            <ZoomOut />
          </Button>
          <span className="min-w-12 text-center text-xs" aria-live="polite">
            {zoomPercentage}%
          </span>
          <Button
            onClick={zoomIn}
            variant="ghost"
            size="icon-sm"
            aria-label={t('navigation.zoomIn')}
            disabled={!canZoomIn}
          >
            <ZoomIn />
          </Button>
        </div>

        <PdfPagesScrollArea containerRef={containerRef} className="py-16" />
        {isRendering && (
          <output
            aria-label={t('app.loading')}
            className="absolute inset-0 flex justify-center px-4 py-16"
          >
            <Skeleton className="h-full max-w-full aspect-[8.5/11] rounded-none bg-muted-foreground/20" />
          </output>
        )}
        {renderOverlay?.({ pageElements, scale, scrollToPage })}
      </div>
    </div>
  );
};
